import type { ReactNode } from 'react';
import { Box, Typography, type SxProps, type Theme } from '@mui/material';

export type FlowDetailSectionHeaderAccent = 'error' | 'secondary' | 'info' | 'warning';

function resolveAccentColor(theme: Theme, accent: FlowDetailSectionHeaderAccent) {
  switch (accent) {
    case 'error':
      return theme.palette.error.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'secondary':
      return theme.palette.secondary.main;
    case 'info':
    default:
      return theme.palette.info.main;
  }
}

export interface FlowDetailSectionHeaderProps {
  title: string;
  ariaLabel?: string;
  accent?: FlowDetailSectionHeaderAccent;
  actions?: ReactNode;
  sx?: SxProps;
}

/** Toolbar-style section header for flow detail tabs (replaces filled Alert banners). */
export function FlowDetailSectionHeader({
  title,
  ariaLabel,
  accent = 'info',
  actions,
  sx,
}: FlowDetailSectionHeaderProps) {
  return (
    <Box
      role="region"
      aria-label={ariaLabel ?? title}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: actions ? 'space-between' : 'flex-start',
        gap: 1,
        minHeight: 32,
        px: 1.5,
        py: 0.5,
        bgcolor: 'background.paper',
        border: 1,
        borderColor: 'divider',
        boxShadow: (theme) => `inset 3px 0 0 ${resolveAccentColor(theme, accent)}`,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, flex: actions ? 1 : undefined }}>
        <Typography variant="subtitle1" fontWeight={600} color="text.primary" noWrap>
          {title}
        </Typography>
      </Box>
      {actions ? <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>{actions}</Box> : null}
    </Box>
  );
}
